/**
 * Add-to-cart confirmation toast.
 *
 * Markup and the reasoning behind the placement live in snippets/cs-cart-toast.liquid.
 * This file is the controller, and it has one job that is harder than it looks:
 * naming the thing that was just added.
 *
 * Shopify's `shopify:cart:lines-update` result carries line ids, quantities and
 * money — no titles, no images. Rather than thread product data through every
 * caller (the product form, the sticky bar, quick-add, an app), the toast works
 * it out after the fact: it holds a baseline of key -> quantity, fetches a fresh
 * /cart.js once the add has settled, and shows whichever line is new or grew.
 * One small request per add, none per page load, and every add path is covered
 * without knowing anything about it.
 *
 * The ordering lesson from cs-product.js and cs-cart-count.js applies here too:
 * `shopify:cart:lines-update` is an OPTIMISTIC event. Nothing is read from the
 * event itself — only from the promise it carries, and then only from a
 * /cart.js that is fetched after that promise settles.
 */
(function () {
  const root = document.querySelector('[data-cs-toast]');
  if (!root) return;

  /** How long the toast stays up with no interaction. */
  const DURATION = 6000;

  const panel = root.querySelector('[data-cs-toast-panel]');
  const imageEl = root.querySelector('[data-cs-toast-image]');
  const titleEl = root.querySelector('[data-cs-toast-title]');
  const metaEl = root.querySelector('[data-cs-toast-meta]');
  const metaRow = root.querySelector('[data-cs-toast-meta-row]');
  const priceEl = root.querySelector('[data-cs-toast-price]');
  const countEl = root.querySelector('[data-cs-toast-count]');
  const barEl = root.querySelector('[data-cs-toast-bar]');
  const closeEl = root.querySelector('[data-cs-toast-close]');

  // --- Baseline -------------------------------------------------------------

  /** @type {Map<string, number>} line key -> quantity, as of the last look. */
  let baseline = new Map();
  let moneyFormat = '${{amount}}';

  try {
    const seed = JSON.parse(document.querySelector('[data-cs-cart-snapshot]').textContent);
    (seed.items || []).forEach((item) => baseline.set(item.key, item.quantity));
    if (seed.money_format) moneyFormat = seed.money_format;
  } catch (error) {
    // A malformed snapshot only costs the first toast its "which line grew"
    // answer — the diff below treats an empty baseline as "everything is new",
    // which for a first add is exactly right anyway.
    console.warn('[cs-cart-toast] could not read the cart snapshot:', error);
  }

  const rebase = (cart) => {
    baseline = new Map((cart.items || []).map((item) => [item.key, item.quantity]));
  };

  // --- Money ----------------------------------------------------------------

  /**
   * Format cents with the shop's own money format.
   *
   * /cart.js returns raw cents and no formatted string, and the theme's prices
   * are rendered by Liquid's `money` filter everywhere else — so the toast has
   * to reproduce it or it will be the one place on the site showing "4908".
   *
   * Exposed because cs-quick-add.js prices variants from /products/<handle>.js,
   * which has the same gap.
   *
   * @param {number} cents
   * @param {string} [format]
   */
  window.csFormatMoney = function (cents, format) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    const group = (number, decimals, thousands, decimal) => {
      const fixed = (Math.abs(number) / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    return (format || moneyFormat).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
      switch (name) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', ',');
        default:
          return group(value, 2, ',', '.');
      }
    });
  };

  // --- Show / hide ----------------------------------------------------------

  let timer = 0;
  let deadline = 0;
  let remaining = DURATION;

  const stopTimer = () => {
    clearTimeout(timer);
    timer = 0;
  };

  const startTimer = (ms) => {
    stopTimer();
    remaining = ms;
    deadline = Date.now() + ms;
    timer = setTimeout(hide, ms);
  };

  const pause = () => {
    if (!timer) return;
    stopTimer();
    remaining = Math.max(0, deadline - Date.now());
    root.dataset.paused = 'true';
  };

  const resume = () => {
    if (root.dataset.state !== 'open' || timer) return;
    delete root.dataset.paused;
    startTimer(remaining || DURATION);
  };

  function hide() {
    stopTimer();
    if (root.dataset.state === 'closed') return;
    root.dataset.state = 'closed';
    delete root.dataset.paused;

    // Wait out the exit transition before going `hidden`, or the panel vanishes
    // instead of sliding. Falls through on transitioncancel too, so a toast
    // re-opened mid-exit is never left hidden.
    const done = () => {
      if (root.dataset.state === 'closed') root.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    panel.addEventListener('transitionend', done);
    setTimeout(done, 400);
  }

  /**
   * @param {{image?: string, title: string, meta?: string, price?: string, count?: number}} item
   */
  const show = (item) => {
    if (item.image) {
      imageEl.src = item.image;
      imageEl.parentElement.hidden = false;
    } else {
      imageEl.removeAttribute('src');
      imageEl.parentElement.hidden = true;
    }

    titleEl.textContent = item.title || '';

    // The variant and quantity stay in the body face; only the price is a
    // figure, so the two are separate nodes rather than one joined string.
    metaEl.textContent = item.meta ? item.meta + ' · ' : '';
    priceEl.textContent = item.price || '';
    metaRow.hidden = !item.meta && !item.price;
    countEl.textContent = item.count ? ' (' + item.count + ')' : '';

    root.hidden = false;
    // Restart the timer bar. Re-adding the class alone does nothing if it never
    // left, so the animation is knocked off and forced to reflow first.
    barEl.style.animation = 'none';
    void barEl.offsetWidth;
    barEl.style.animation = '';
    barEl.style.setProperty('--cs-toast-duration', DURATION + 'ms');

    // One frame between `hidden = false` and the open state, so the panel has a
    // closed position to transition FROM.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.dataset.state = 'open';
      });
    });

    startTimer(DURATION);
  };

  // --- The diff -------------------------------------------------------------

  /**
   * Fetch the settled cart, work out what changed, and say so.
   *
   * Exposed so cs-quick-add.js — which adds through /cart/add.js directly and so
   * never fires Horizon's event — can announce its own adds through the same
   * path rather than growing a second toast.
   */
  let announcing = null;

  window.csCartToast = {
    hide,
    /** @returns {Promise<void>} */
    announce() {
      // Two adds landing together (a double click, or an app echoing the form)
      // should be one toast showing the settled cart, not two racing fetches.
      if (announcing) return announcing;

      announcing = fetch(window.Theme?.routes?.cart_url ? window.Theme.routes.cart_url + '.js' : '/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      })
        .then((response) => {
          if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
          return response.json();
        })
        .then((cart) => {
          const items = cart.items || [];

          // Prefer a line that did not exist before; otherwise the line that
          // grew the most. Shopify appends new lines, so the last match wins a
          // tie — that is the one the customer just clicked.
          let best = null;
          let bestGain = 0;

          items.forEach((item) => {
            const before = baseline.get(item.key);
            const isNew = before === undefined;
            const gain = isNew ? item.quantity : item.quantity - before;
            if (gain <= 0) return;
            if (!best || isNew || gain >= bestGain) {
              best = { item, isNew, gain };
              bestGain = gain;
            }
          });

          rebase(cart);
          if (typeof window.csPaintCartCount === 'function') window.csPaintCartCount(cart.item_count);

          // Nothing grew: an add that stock limits refused, or a quantity edit.
          // Claiming "Added to cart" there would be a lie.
          if (!best) return;

          const variant =
            best.item.variant_title && best.item.variant_title !== 'Default Title'
              ? best.item.variant_title
              : '';
          const qty = best.gain > 1 ? 'Qty ' + best.gain : '';
          const price = window.csFormatMoney(best.item.price * best.gain);

          show({
            image: best.item.featured_image?.url || best.item.image || '',
            title: best.item.product_title || best.item.title || '',
            meta: [variant, qty].filter(Boolean).join(' · '),
            price,
            count: cart.item_count,
          });
        })
        .catch((error) => {
          console.warn('[cs-cart-toast] could not read the cart:', error);
        })
        .finally(() => {
          announcing = null;
        });

      return announcing;
    },
  };

  // --- Wiring ---------------------------------------------------------------

  closeEl?.addEventListener('click', hide);
  panel.addEventListener('pointerenter', pause);
  panel.addEventListener('pointerleave', resume);
  panel.addEventListener('focusin', pause);
  panel.addEventListener('focusout', resume);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.dataset.state === 'open') hide();
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    // Horizon's own cart drawer, if it is ever set to auto-open, is feedback
    // enough — two confirmations for one click is worse than one.
    if (document.querySelector('cart-drawer-component[auto-open]')) return;

    if (!event.promise) return;
    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;
        window.csCartToast.announce();
      })
      .catch(() => {});
  });
})();
